import { QUESTION_BODY_MAX, QUESTION_SUBJECT_MAX, questionCreateSchema } from '@fruuit/shared'
import { Button, Callout, Dialog, DialogCancel, Input, Textarea, useToast } from '@fruuit/ui'
import { type FormEvent, useState } from 'react'
import { api } from '../../api/client'
import { useApiMutation } from '../../api/hooks'
import { type FieldErrors, serverErrors, validate } from '../engagements/forms'
import { LANE_D_KEYS } from '../engagements/queries'

export interface AskManagerDialogProps {
  open: boolean
  onClose: () => void
  engagementId: string
  managerName: string
  companyName: string
}

/**
 * "Ask the manager" (P-ENG-001, GAP-UI-029: investors only). Starts a thread on the case;
 * the manager replies in the workspace. Nothing is emailed.
 */
export function AskManagerDialog({
  open,
  onClose,
  engagementId,
  managerName,
  companyName,
}: AskManagerDialogProps) {
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [errors, setErrors] = useState<FieldErrors>({})
  const { toast } = useToast()

  function close() {
    setErrors({})
    onClose()
  }

  const mutation = useApiMutation(
    (input: { engagement_id: string; subject?: string; body: string }, { requestId }) =>
      api.post('/questions', input, { requestId }),
    {
      invalidate: LANE_D_KEYS,
      onSuccess: () => {
        setSubject('')
        setBody('')
        close()
        toast({ tone: 'success', message: `Your question is with ${managerName}.` })
      },
      onError: (error) => setErrors(serverErrors(error).fields),
    },
  )
  const formError = mutation.error ? serverErrors(mutation.error).message : null

  function submit(event: FormEvent) {
    event.preventDefault()
    const checked = validate(questionCreateSchema, {
      engagement_id: engagementId,
      body,
      ...(subject.trim() ? { subject } : {}),
    })
    if (!checked.ok) {
      setErrors(checked.errors)
      return
    }
    setErrors({})
    mutation.mutate(checked.data)
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Ask the manager"
      description={`Questions about the evidence, company response or next step for ${companyName} stay with this case.`}
      footer={
        <>
          <DialogCancel />
          <Button type="submit" form="ask-manager-form" loading={mutation.isPending}>
            Send question
          </Button>
        </>
      }
    >
      <form id="ask-manager-form" onSubmit={submit} noValidate className="space-y-4">
        <Input
          label="Subject"
          hint="Optional. We use the first line of your question if you leave it empty."
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          maxLength={QUESTION_SUBJECT_MAX}
          error={errors.subject}
        />
        <Textarea
          label="Your question"
          required
          rows={5}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          maxLength={QUESTION_BODY_MAX}
          error={errors.body}
        />
        <p className="text-xs leading-relaxed text-sage">
          Visible to you, your organisation's administrator and Fruuit's engagement team. No email
          is sent.
        </p>
        {formError ? (
          <Callout tone="danger" role="alert">
            {formError}
          </Callout>
        ) : null}
      </form>
    </Dialog>
  )
}
